import React, { useEffect, useState } from "react";
import { fetchMonthlyThroughputDetails } from "../api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useTheme } from '@mui/material/styles';
import { getMonthDays } from "../utils/month";
import { useTranslation } from "react-i18next";
import { useCustomerLocale } from "../context/CustomerSettingsContext";
import { parseLocalDateTime } from "../utils/localDateTime";

const MonthlyThroughputsChart = ({ date, onClick, onLoadingChange }) => {
  const [data, setData] = useState([]);
  const [workcellNames, setWorkcellNames] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const locale = useCustomerLocale();

  const formatInt = (value) => {
    const num = Number(value);
    const safe = Number.isFinite(num) ? num : 0;
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(safe);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!date) {
      setData([]);
      onLoadingChange?.(false);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    onLoadingChange?.(true);
    fetchMonthlyThroughputDetails(date)
      .then((apiData) => {
        if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
          setData([]);
          return;
        }
        // Create a full month dataset with default 0 values for each workcell
        const workcells = [...new Set(apiData.map((entry) => entry.workcell_id))];
        const workcellNamesMap = {};
        apiData.forEach((entry) => {
          workcellNamesMap[entry.workcell_id] = entry.workcell_name;
        });
        setWorkcellNames(workcellNamesMap);

        const days = getMonthDays(date);
        const fullMonthData = days.map((day) => {
          const entry = { day };
          workcells.forEach((workcell) => {
            entry[workcell] = 0;
          });
          return entry;
        });

        // Merge API data into the full dataset
        apiData.forEach((entry) => {
          const index = fullMonthData.findIndex((d) => d.day === entry.day);
          if (index !== -1) {
            fullMonthData[index][entry.workcell_id] = entry.work_done; // Replace 0 with actual value
          }
        });

        setData(fullMonthData);
      })
      .catch((error) => {
        console.error(error);
        setData([]);
      })
      .finally(() => {
        onLoadingChange?.(false);
        setIsLoading(false);
      });
  }, [date]);

  // Sort workcells by total throughput to match table order
  const sortedWorkcells = data.length > 0
    ? Object.keys(data[0] || {})
        .filter(key => key !== "day")
        .sort((a, b) => {
          const totalA = data.reduce((sum, entry) => sum + (entry[a] || 0), 0);
          const totalB = data.reduce((sum, entry) => sum + (entry[b] || 0), 0);
          return totalB - totalA;
        })
    : [];

  if (isLoading) return <div>{t("common.loading")}</div>;
  if (data.length === 0) return <div>{t("common.noData")}</div>;

  // Custom tick for month name (only show for first day of month)
  const MonthTick = (props) => {
    const { x, y, payload, index } = props;
    const tickDate = parseLocalDateTime(payload.value);
    const isFirst = index === 0;
    const prevDate = index > 0 ? parseLocalDateTime(data[index - 1].day) : null;
    const isMonthStart = isFirst || (prevDate && prevDate.getMonth() !== tickDate.getMonth());
    return isMonthStart ? (
      <g>
        <text x={x} y={y} textAnchor="middle" fontSize="12" fill="#666">
          {tickDate.toLocaleDateString(locale, { month: 'short' })}
        </text>
      </g>
    ) : null;
  };


  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        {/* Main X axis: days */}
        <XAxis
          dataKey="day"
          tickFormatter={(tick) => parseLocalDateTime(tick)?.getDate() || ''}
          height={30}
          axisLine={true}
          tickLine={true}
        />
        {/* Secondary X axis: month name */}
        <XAxis
          dataKey="day"
          axisLine={false}
          tickLine={false}
          height={20}
          xAxisId="month"
          tick={MonthTick}
          interval={0}
          allowDataOverflow={true}
          orientation="bottom"
        />
        <YAxis
          label={{
            value: t("monthly.plannings.throughputChart.axisLabel"),
            angle: -90,
            position: "insideLeft",
            style: { textAnchor: "middle" }
          }}
        />
        <Tooltip formatter={(value) => formatInt(value)} />
        {sortedWorkcells.map((workcell, index) => (
          <Bar
            key={workcell}
            dataKey={workcell}
            name={workcellNames[workcell]}
            stackId="a"
            fill={theme.palette.chart.colors[index % theme.palette.chart.colors.length]}
            onClick={(data) => {
              if (onClick) onClick(data.day)
            }}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default MonthlyThroughputsChart;
